// Parity P1 wave 1: the task detail's own reads (layouts, watchers, field history, metric
// observations) and the per-person preferences (`me/prefs`, design §3.1). Keys sit under the
// Work prefix, so a Work write's invalidation reaches them; prefs sit outside it (a task write
// never refetches a preference).
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../../api/client";

export type BoardTaskLayout = OperationOutput<"taskLayout.board.get">;
export type SpaceTaskLayout = OperationOutput<"taskLayout.space.get">;
export type TaskWatchers = OperationOutput<"tasks.watchers">;
export type FieldHistoryEntry = OperationOutput<"tasks.fieldHistory">["data"][number];
export type TaskObservation = OperationOutput<"tasks.observations.list">["data"][number];
export type FieldRule = BoardTaskLayout["fields"][number];

export const p1Keys = {
  boardLayout: (co: string, board: string) =>
    ["company", co, "work", "board", board, "task-layout"] as const,
  boardLayoutHistory: (co: string, board: string) =>
    ["company", co, "work", "board", board, "task-layout", "history"] as const,
  spaceLayout: (co: string, space: string) =>
    ["company", co, "work", "space", space, "task-layout"] as const,
  spaceLayoutHistory: (co: string, space: string) =>
    ["company", co, "work", "space", space, "task-layout", "history"] as const,
  watchers: (co: string, task: string) =>
    ["company", co, "work", "task", task, "watchers"] as const,
  fieldHistory: (co: string, task: string, field: string) =>
    ["company", co, "work", "task", task, "field-history", field] as const,
  observations: (co: string, task: string) =>
    ["company", co, "work", "task", task, "observations"] as const,
  pref: (co: string, key: string) => ["company", co, "me", "prefs", key] as const,
};

/** A task's P1 reads a change ping refreshes (task-cache.ts `patchTaskPing`). */
export const p1TaskKeys = (co: string, task: string) => [
  p1Keys.watchers(co, task),
  ["company", co, "work", "task", task, "field-history"] as const,
  p1Keys.observations(co, task),
];

/** A layout changes rarely: the drawer keeps it a minute before it checks again. */
const LAYOUT_STALE = 60_000;

export const boardLayoutQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: p1Keys.boardLayout(co, board),
    queryFn: () => api.client.taskLayout.board.get({ params: { co, id: board } }),
    staleTime: LAYOUT_STALE,
  });

export const boardLayoutHistoryQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: p1Keys.boardLayoutHistory(co, board),
    queryFn: () => api.client.taskLayout.board.history({ params: { co, id: board } }),
  });

export const spaceLayoutQuery = (api: Api, co: string, space: string) =>
  queryOptions({
    queryKey: p1Keys.spaceLayout(co, space),
    queryFn: () => api.client.taskLayout.space.get({ params: { co, id: space } }),
  });

export const spaceLayoutHistoryQuery = (api: Api, co: string, space: string) =>
  queryOptions({
    queryKey: p1Keys.spaceLayoutHistory(co, space),
    queryFn: () => api.client.taskLayout.space.history({ params: { co, id: space } }),
  });

export const watchersQuery = (api: Api, co: string, task: string) =>
  queryOptions({
    queryKey: p1Keys.watchers(co, task),
    queryFn: () => api.client.tasks.watchers({ params: { co, id: task } }),
  });

export const fieldHistoryQuery = (api: Api, co: string, task: string, field: string) =>
  queryOptions({
    queryKey: p1Keys.fieldHistory(co, task, field),
    queryFn: () =>
      api.client.tasks.fieldHistory({ params: { co, id: task }, query: { field_id: field } }),
  });

export const observationsQuery = (api: Api, co: string, task: string) =>
  queryOptions({
    queryKey: p1Keys.observations(co, task),
    queryFn: () => api.client.tasks.observations.list({ params: { co, id: task }, query: {} }),
  });

export type TaskDrawerPref = {
  rail?: "open" | "closed";
  collapsed?: string[];
  /** The conversation's share of the host's width (0.3–0.7; default 0.5). */
  rail_share?: number;
};
export type TaskFilesPref = { view?: "grid" | "list"; sort?: "name" | "date" | "size" | "type" };
/** WORKFORCE-BOARDVIEW: the tab each board opens on for this person (board id → view kind). */
export type BoardViewPref = {
  kinds?: Record<string, "board" | "list" | "table" | "calendar" | "timeline" | "gallery">;
};
/** HOME-DASH: the person's Home layout (features/home/home-layout.ts reads it). */
export type HomeLayoutPref = {
  widgets?: { id: string; hidden?: boolean; size?: "xs" | "s" | "m" | "l" | "xl" | "full" }[];
};
type Prefs = {
  task_drawer: TaskDrawerPref;
  task_files: TaskFilesPref;
  board_view: BoardViewPref;
  home_layout: HomeLayoutPref;
};

/**
 * One preference: its value (`fallback` until loaded or when never set), a setter that shows
 * the new value at once and saves it behind (a failed save puts the old value back), and
 * whether the stored value has been read (true after a failed read too: the fallback stands).
 */
export function usePref<K extends keyof Prefs>(
  api: Api,
  co: string,
  key: K,
  fallback: Prefs[K],
): [
  Prefs[K],
  (next: Prefs[K], on?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => void,
  boolean,
] {
  const qc = useQueryClient();
  const queryKey = p1Keys.pref(co, key);
  const q = useQuery({
    queryKey,
    queryFn: async () =>
      ((await api.client.me.prefs.get({ params: { co, key } })).value ?? null) as Prefs[K] | null,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const save = useMutation({
    mutationFn: (value: Prefs[K]) =>
      api.client.me.prefs.put({ params: { co, key }, body: { value } }),
    onMutate: (value) => {
      const before = qc.getQueryData(queryKey);
      qc.setQueryData(queryKey, value);
      return { before };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(queryKey, ctx?.before ?? null),
  });
  // `on` (optional): told once the save answered — a caller confirming the save waits for it.
  return [
    q.data ?? fallback,
    (next, on) =>
      save.mutate(next, {
        ...(on?.onSuccess ? { onSuccess: () => on.onSuccess?.() } : {}),
        ...(on?.onError ? { onError: (e: unknown) => on.onError?.(e) } : {}),
      }),
    !q.isPending,
  ];
}
